import { useEffect, useMemo, useState } from 'react'
import Layout from '../components/Layout'
import {
  fetchParentsPage,
  fetchChildrenPage,
  ADMIN_PAGE_SIZE,
  createParentAccount,
  updateParentBilling,
  startTrial,
  activateMonthly,
  suspendParent,
  deleteParent,
  setParentName,
  subscribePaymentConfig,
  updatePaymentConfig,
  subscribeUpdateConfig,
  publishUpdateConfig,
  unlinkChildFromParent,
  deleteChild,
  assignChildToParent,
  subscribePaymentRequests,
  resolvePaymentRequest,
  activateMonthly as activateMonthlyDb,
  subscribeAllAdmins,
  removeAdmin,
  ensureAdminMetadata,
} from '../lib/db'
import { auth } from '../firebase'
import { effectiveStatus, daysRemaining, statusLabel } from '../lib/billing'
import { formatPrice, getStoredCurrency, setStoredCurrency, CURRENCIES, PLAN_PRICES, deviceLimitFor, type CurrencyCode } from '../lib/currency'
import { toast } from '../lib/toast'
import type { Parent, BillingStatus, BillingInfo, Child, PaymentGatewayConfig } from '../lib/types'
import type { NavDest } from '../lib/nav'

export default function AdminPage({
  onOpenAccount,
  onOpenDownloads,
  onNavigate,
}: {
  onOpenAccount?: () => void
  onOpenDownloads?: () => void
  onNavigate: (dest: NavDest) => void
}) {
  const [parents, setParents] = useState<{ uid: string; data: Parent }[]>([])
  const [children, setChildren] = useState<{ uid: string; data: import('../lib/types').Child }[]>([])
  const [requests, setRequests] = useState<import('../lib/db').PaymentRequest[]>([])
  const [showCreate, setShowCreate] = useState(false)
  const [newName, setNewName] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [creating, setCreating] = useState(false)
  const [createdCreds, setCreatedCreds] = useState<{ email: string; password: string } | null>(null)
  const [error, setError] = useState('')
  const [currency, setCurrency] = useState<CurrencyCode>(getStoredCurrency())
  const [showPayConfig, setShowPayConfig] = useState(false)
  const [payConfig, setPayConfig] = useState<PaymentGatewayConfig>({})
  const [savingPayConfig, setSavingPayConfig] = useState(false)
  const [updateCfg, setUpdateCfg] = useState<import('../lib/types').UpdateConfig | null>(null)
  const [updTarget, setUpdTarget] = useState<import('../lib/types').UpdateTarget>('all')
  const [updNotes, setUpdNotes] = useState('')
  const [updWinVer, setUpdWinVer] = useState('1.8.6')
  const [updAndVer, setUpdAndVer] = useState('1.8.6')
  const [publishingUpd, setPublishingUpd] = useState(false)
  const [agentLatest, setAgentLatest] = useState('')
  const [adminList, setAdminList] = useState<import('../lib/db').AdminEntry[]>([])
  const [search, setSearch] = useState('')

  const changeCurrency = (code: CurrencyCode) => {
    setCurrency(code)
    setStoredCurrency(code)
  }

  const [parentsLastKey, setParentsLastKey] = useState<string | null>(null)
  const [hasMoreParents, setHasMoreParents] = useState(false)
  const [childrenLastKey, setChildrenLastKey] = useState<string | null>(null)
  const [hasMoreChildren, setHasMoreChildren] = useState(false)
  const [loadingTables, setLoadingTables] = useState(false)

  // Tablas por páginas (GET puntuales): evita descargar toda la base en cada
  // cambio de telemetría. Tiempo real solo para solicitudes de pago.
  const loadTables = async (reset = true) => {
    setLoadingTables(true)
    try {
      const [pp, cp] = await Promise.all([fetchParentsPage(), fetchChildrenPage()])
      if (reset) {
        setParents(pp.rows)
        setChildren(cp.rows)
      } else {
        setParents((prev) => [...prev, ...pp.rows.filter((r) => !prev.some((p) => p.uid === r.uid))])
        setChildren((prev) => [...prev, ...cp.rows.filter((r) => !prev.some((c) => c.uid === r.uid))])
      }
      setParentsLastKey(pp.lastKey)
      setHasMoreParents(pp.hasMore)
      setChildrenLastKey(cp.lastKey)
      setHasMoreChildren(cp.hasMore)
    } catch (e: unknown) {
      toast((e as Error).message ?? 'No se pudieron cargar las tablas', 'error')
    } finally {
      setLoadingTables(false)
    }
  }

  const loadMoreParents = async () => {
    if (!hasMoreParents || !parentsLastKey) return
    setLoadingTables(true)
    try {
      const pp = await fetchParentsPage(ADMIN_PAGE_SIZE, parentsLastKey)
      setParents((prev) => [...prev, ...pp.rows.filter((r) => !prev.some((p) => p.uid === r.uid))])
      setParentsLastKey(pp.lastKey)
      setHasMoreParents(pp.hasMore)
    } finally {
      setLoadingTables(false)
    }
  }

  const loadMoreChildren = async () => {
    if (!hasMoreChildren || !childrenLastKey) return
    setLoadingTables(true)
    try {
      const cp = await fetchChildrenPage(ADMIN_PAGE_SIZE, childrenLastKey)
      setChildren((prev) => [...prev, ...cp.rows.filter((r) => !prev.some((c) => c.uid === r.uid))])
      setChildrenLastKey(cp.lastKey)
      setHasMoreChildren(cp.hasMore)
    } finally {
      setLoadingTables(false)
    }
  }

  useEffect(() => {
    loadTables()
    const un3 = subscribePaymentRequests(setRequests)
    const un4 = subscribePaymentConfig((cfg) => setPayConfig(cfg ?? {}))
    const un6 = subscribeAllAdmins(setAdminList)
    // Versiones de AGENTE: la última publicada en GitHub (fuente de verdad del release)
    fetch('https://api.github.com/repos/JAndresOspina/KidSafe/releases/latest', { headers: { Accept: 'application/vnd.github+json' } })
      .then((r) => r.ok ? r.json() : null)
      .then((j) => {
        if (j?.tag_name) {
          const latest = j.tag_name.replace(/^v/, '')
          setAgentLatest(latest)
          // Precargar inputs con la versión actual publicada (NO con el último push,
          // que puede ser una versión vieja que los agentes ya ignoran)
          setUpdWinVer((prev) => (prev === '1.8.6' ? latest : prev))
          setUpdAndVer((prev) => (prev === '1.8.6' ? latest : prev))
        }
      })
      .catch(() => {})
    const un5 = subscribeUpdateConfig((cfg) => setUpdateCfg(cfg))
    return () => {
      un3()
      un4()
      un5()
      un6()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const savePaymentConfig = async () => {
    setSavingPayConfig(true)
    try {
      await updatePaymentConfig(payConfig)
      toast('✅ Configuración de pagos guardada', 'success')
    } finally {
      setSavingPayConfig(false)
    }
  }

  // Auto-registrar email/nombre del admin actual en users/admins (trazabilidad:
  // así la sección Administradores muestra el correo, no solo el uid opaco)
  useEffect(() => {
    const user = auth.currentUser
    if (!user?.uid) return
    const myEntry = adminList.find((a) => a.uid === user.uid)
    if (!myEntry) return
    const parentInfo = parents.find((p) => p.uid === user.uid)
    ensureAdminMetadata(
      user.uid,
      myEntry.email ?? parentInfo?.data.email ?? user.email ?? null,
      myEntry.name ?? parentInfo?.data.name ?? null,
    ).catch(() => {})
  }, [adminList, parents])

  const handlePublishUpdate = async () => {
    if (!updWinVer.trim() || !updAndVer.trim()) { toast('Indica versiones', 'error'); return }
    setPublishingUpd(true)
    try {
      await publishUpdateConfig(updTarget, updNotes, updWinVer.trim(), updAndVer.trim(), auth.currentUser?.uid ?? 'admin')
      toast(`🚀 Actualización ${updTarget} v${updWinVer}/${updAndVer} enviada - agentes se actualizarán en <5 min`, 'success')
    } catch (e: unknown) { toast((e as Error).message, 'error') } finally { setPublishingUpd(false) }
  }

  const handleActivate = async (uid: string, tier: 'premium' | 'familia') => {
    try {
      await activateMonthlyDb(uid, tier)
      await resolvePaymentRequest(uid)
      toast('✅ Plan activado', 'success')
      await loadTables()
    } catch (e: unknown) {
      toast((e as Error).message ?? 'No se pudo activar el plan', 'error')
    }
  }

  const currentUserUid = auth.currentUser?.uid ?? ''

  const stats = useMemo(() => {
    const counts: Record<BillingStatus, number> = { active: 0, trial: 0, expired: 0, suspended: 0 }
    parents.forEach((p) => {
      const rowIsAdmin = p.uid === currentUserUid
      counts[effectiveStatus(p.data.billing, rowIsAdmin)]++
    })
    return counts
  }, [parents, currentUserUid])

  // Buscador de clientes (nombre/email). Filtra en cliente sobre lo cargado.
  const filteredParents = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return parents
    return parents.filter(
      ({ data }) =>
        (data.name ?? '').toLowerCase().includes(q) ||
        (data.email ?? '').toLowerCase().includes(q),
    )
  }, [parents, search])

  const handleCreate = async () => {
    setError('')
    setCreating(true)
    try {
      const { password } = await createParentAccount(newName, newEmail)
      setCreatedCreds({ email: newEmail, password })
      setNewName('')
      setNewEmail('')
      setShowCreate(false)
      await loadTables()
    } catch (e: unknown) {
      setError((e as Error).message)
    } finally {
      setCreating(false)
    }
  }

  return (
    <Layout title="Panel SuperAdmin" onNavigate={onNavigate} onAccount={onOpenAccount} onDownloads={onOpenDownloads}>
      {createdCreds && (
        <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
          <h3 className="font-bold text-emerald-800">Cliente creado 🎉</h3>
          <p className="mt-1 text-sm text-emerald-700">
            Entrega estas credenciales al cliente (trial de 14 días ya activado):
          </p>
          <div className="mt-3 rounded-xl bg-white p-4 font-mono text-sm ring-1 ring-emerald-200">
            <p>Email: <b>{createdCreds.email}</b></p>
            <p>Contraseña: <b>{createdCreds.password}</b></p>
          </div>
          <button
            onClick={() => setCreatedCreds(null)}
            className="mt-3 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            Cerrar
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <AdminStat label="Clientes" value={parents.length} icon="👥" />
        <AdminStat label="Activos" value={stats.active} icon="✅" accent="text-emerald-600" />
        <AdminStat label="En trial" value={stats.trial} icon="🕑" accent="text-sky-600" />
        <AdminStat label="Vencidos" value={stats.expired} icon="⏰" accent="text-rose-600" />
        <AdminStat label="Suspendidos" value={stats.suspended} icon="⛔" accent="text-amber-600" />
      </div>

      {/* Configuración de pagos */}
      <div className="mt-6">
        <button
          onClick={() => setShowPayConfig(!showPayConfig)}
          className="flex w-full items-center justify-between rounded-2xl bg-white p-4 text-left ring-1 ring-slate-200 hover:bg-slate-50"
        >
          <span className="font-bold text-slate-800">
            ⚙️ Configuración de Pagos
          </span>
          <span className="text-slate-400">{showPayConfig ? '▲' : '▼'}</span>
        </button>
        {showPayConfig && (
          <div className="mt-3 space-y-4 rounded-2xl bg-white p-5 ring-1 ring-slate-200">
            <div>
              <h4 className="mb-2 text-sm font-bold text-slate-600">Pasarelas de pago</h4>
              <p className="mb-2 text-xs text-slate-400">
                Genéricos (cualquier plan). Abajo puedes poner un link con monto exacto por plan.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <input
                  placeholder="Enlace Wompi (https://checkout.wompi.co/l/…)"
                  value={payConfig.wompiUrl ?? ''}
                  onChange={(e) => setPayConfig({ ...payConfig, wompiUrl: e.target.value })}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
                <input
                  placeholder="Enlace MercadoPago (https://mpago.la/…)"
                  value={payConfig.mercadoPagoUrl ?? ''}
                  onChange={(e) => setPayConfig({ ...payConfig, mercadoPagoUrl: e.target.value })}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
              </div>
              <h4 className="mb-2 mt-4 text-sm font-bold text-slate-600">Links por plan (monto exacto)</h4>
              <p className="mb-2 text-xs text-slate-400">
                Crea en Wompi/MP un link por plan con su precio exacto. El panel abre el del plan elegido.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <input
                  placeholder="Wompi Premium (monto exacto Premium)"
                  value={payConfig.wompiPremiumUrl ?? ''}
                  onChange={(e) => setPayConfig({ ...payConfig, wompiPremiumUrl: e.target.value })}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
                <input
                  placeholder="Wompi Familia (monto exacto Familia)"
                  value={payConfig.wompiFamiliaUrl ?? ''}
                  onChange={(e) => setPayConfig({ ...payConfig, wompiFamiliaUrl: e.target.value })}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
                <input
                  placeholder="MercadoPago Premium (monto exacto Premium)"
                  value={payConfig.mercadoPagoPremiumUrl ?? ''}
                  onChange={(e) => setPayConfig({ ...payConfig, mercadoPagoPremiumUrl: e.target.value })}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
                <input
                  placeholder="MercadoPago Familia (monto exacto Familia)"
                  value={payConfig.mercadoPagoFamiliaUrl ?? ''}
                  onChange={(e) => setPayConfig({ ...payConfig, mercadoPagoFamiliaUrl: e.target.value })}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
              </div>
            </div>
            <div>
              <h4 className="mb-2 text-sm font-bold text-slate-600">Transferencia bancaria</h4>
              <div className="grid gap-3 sm:grid-cols-2">
                <input
                  placeholder="Banco (ej: Bancolombia)"
                  value={payConfig.bankName ?? ''}
                  onChange={(e) => setPayConfig({ ...payConfig, bankName: e.target.value })}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
                <input
                  placeholder="Tipo de cuenta (ej: Ahorros)"
                  value={payConfig.accountType ?? ''}
                  onChange={(e) => setPayConfig({ ...payConfig, accountType: e.target.value })}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
                <input
                  placeholder="Número de cuenta"
                  value={payConfig.accountNumber ?? ''}
                  onChange={(e) => setPayConfig({ ...payConfig, accountNumber: e.target.value })}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
                <input
                  placeholder="Nombre del beneficiario"
                  value={payConfig.beneficiaryName ?? ''}
                  onChange={(e) => setPayConfig({ ...payConfig, beneficiaryName: e.target.value })}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
                <input
                  placeholder="Documento (ej: CC 123456789)"
                  value={payConfig.documentId ?? ''}
                  onChange={(e) => setPayConfig({ ...payConfig, documentId: e.target.value })}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
              </div>
            </div>
            <div>
              <h4 className="mb-2 text-sm font-bold text-slate-600">Contacto</h4>
              <div className="grid gap-3 sm:grid-cols-2">
                <input
                  placeholder="WhatsApp (573001234567)"
                  value={payConfig.whatsappNumber ?? ''}
                  onChange={(e) => setPayConfig({ ...payConfig, whatsappNumber: e.target.value })}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
                <input
                  placeholder="Email de soporte"
                  value={payConfig.adminEmail ?? ''}
                  onChange={(e) => setPayConfig({ ...payConfig, adminEmail: e.target.value })}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
              </div>
            </div>
            <button
              onClick={savePaymentConfig}
              disabled={savingPayConfig}
              className="rounded-xl bg-brand-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
            >
              {savingPayConfig ? 'Guardando…' : '💾 Guardar configuración'}
            </button>
          </div>
        )}
      </div>

      {/* Forzar actualización agentes */}
      <div className="mt-6 rounded-2xl border border-sky-200 bg-sky-50 p-5">
        <h3 className="font-bold text-sky-800">🚀 Forzar actualización agentes</h3>
        <p className="mt-1 text-sm text-sky-700">Manda orden inmediata. Los agentes escuchan <code>config/updates</code> en Firebase y se actualizan en &lt;1 min (Windows) / &lt;5 s (Android con app abierta). Con <b>forzar</b> reinstalan aunque sea la misma versión.</p>
        {agentLatest && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-sky-800">Última versión publicada en GitHub: <b>v{agentLatest}</b></span>
            <button
              onClick={() => { setUpdWinVer(agentLatest); setUpdAndVer(agentLatest) }}
              className="rounded-lg bg-white px-2.5 py-1 text-xs font-semibold text-sky-700 ring-1 ring-sky-300 hover:bg-sky-100"
            >
              ⚡ Usar v{agentLatest} en ambos
            </button>
          </div>
        )}
        {updateCfg && <p className="mt-2 text-xs text-sky-600">Último push: <b>{updateCfg.target}</b> v{updateCfg.windowsVersion}/{updateCfg.androidVersion} — {updateCfg.notes} ({new Date(updateCfg.updatedAt).toLocaleString()})</p>}
        <div className="mt-3 grid gap-3 sm:grid-cols-4">
          <select value={updTarget} onChange={e=>setUpdTarget(e.target.value as import('../lib/types').UpdateTarget)} className="rounded-xl border border-sky-200 bg-white px-3 py-2 text-sm">
            <option value="all">Ambos agentes</option>
            <option value="windows">Solo Windows</option>
            <option value="android">Solo Android</option>
          </select>
          <input value={updWinVer} onChange={e=>setUpdWinVer(e.target.value)} placeholder="Windows v (ej 1.10.6)" className="rounded-xl border border-sky-200 px-3 py-2 text-sm" />
          <input value={updAndVer} onChange={e=>setUpdAndVer(e.target.value)} placeholder="Android v (ej 1.10.6)" className="rounded-xl border border-sky-200 px-3 py-2 text-sm" />
          <button onClick={handlePublishUpdate} disabled={publishingUpd} className="rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60">{publishingUpd?'Enviando…':'📢 Enviar orden'}</button>
        </div>
        <textarea value={updNotes} onChange={e=>setUpdNotes(e.target.value)} placeholder="Notas para popup del agente (ej: Corrección bloqueo rutina + nuevos filtros)" rows={2} className="mt-3 w-full rounded-xl border border-sky-200 px-3 py-2 text-sm" />
      </div>

      {/* Administradores: quien tiene acceso SuperAdmin */}
      <div className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 p-5">
        <h3 className="font-bold text-rose-800">🛡️ Administradores con acceso SuperAdmin</h3>
        <p className="mt-1 text-sm text-rose-700">
          Usuarios con privilegios de dueño (config de pagos, updates, clientes). <b>Deberías ser solo tú</b>. Si ves una cuenta que no reconoces, elimínala con el botón 🗑️.
        </p>
        <div className="mt-3 space-y-2">
          {adminList.length === 0 && <p className="text-sm text-slate-500">No hay administradores registrados.</p>}
          {adminList.map((a) => {
            const parentInfo = parents.find((p) => p.uid === a.uid)
            const email = a.email ?? parentInfo?.data.email ?? '(email no encontrado en clientes)'
            const name = a.name ?? parentInfo?.data.name ?? ''
            const isSelf = a.uid === (auth.currentUser?.uid ?? '')
            return (
              <div key={a.uid} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-white p-3 ring-1 ring-rose-200">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-800">
                    {name ? `${name} — ` : ''}{email}
                    {isSelf && <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">TÚ (no eliminable)</span>}
                  </p>
                  <p className="truncate font-mono text-[10px] text-slate-400">{a.uid}</p>
                </div>
                <button
                  onClick={() => {
                    if (isSelf) { toast('No puedes eliminar tu propia cuenta de admin', 'error'); return }
                    if (confirm(`¿Quitar privilegios SuperAdmin a ${email}?\n\nPerderá acceso inmediato al panel.`)) {
                      removeAdmin(a.uid)
                        .then(() => toast(`✅ ${email} ya no es administrador`, 'success'))
                        .catch((e: unknown) => toast((e as Error).message, 'error'))
                    }
                  }}
                  disabled={isSelf}
                  className="rounded-lg bg-rose-100 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-200 disabled:opacity-40"
                >
                  🗑️ Quitar admin
                </button>
              </div>
            )
          })}
        </div>
      </div>

      {/* Clientes */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-slate-800">Clientes</h2>
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
            {(Object.keys(CURRENCIES) as CurrencyCode[]).map((code) => (
              <button
                key={code}
                onClick={() => changeCurrency(code)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                  currency === code
                    ? 'bg-brand-600 text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {CURRENCIES[code].flag} {code}
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            + Nuevo cliente
          </button>
        </div>
      </div>

      {requests.length > 0 && (
        <div className="mt-4 rounded-2xl border border-amber-300 bg-amber-50 p-5">
          <h3 className="font-bold text-amber-800">
            💳 Solicitudes de activación ({requests.length})
          </h3>
          <p className="text-xs text-amber-700">
            Clientes que pagaron o pidieron activar su plan. Verifica el pago y activa.
          </p>
          <div className="mt-3 space-y-2">
            {requests.map((r) => (
              <RequestRow
                key={r.uid}
                request={r}
                currency={currency}
                onActivate={handleActivate}
                onDismiss={() => resolvePaymentRequest(r.uid)}
              />
            ))}
          </div>
        </div>
      )}

      {showCreate && (
        <div className="mt-4 rounded-2xl bg-white p-5 ring-1 ring-slate-200">
          <h3 className="font-bold text-slate-800">Crear cliente (trial 14 días)</h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Nombre del cliente"
              className="rounded-xl border border-slate-200 px-4 py-2 outline-none focus:border-brand-500"
            />
            <input
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Correo electrónico"
              type="email"
              className="rounded-xl border border-slate-200 px-4 py-2 outline-none focus:border-brand-500"
            />
            <div className="flex gap-2">
              <button
                onClick={handleCreate}
                disabled={creating || !newName || !newEmail}
                className="flex-1 rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {creating ? 'Creando…' : 'Crear'}
              </button>
              <button
                onClick={() => setShowCreate(false)}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-600"
              >
                Cancelar
              </button>
            </div>
          </div>
          {error && <p className="mt-2 text-sm font-medium text-rose-600">{error}</p>}
        </div>
      )}

      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="🔍 Buscar cliente por nombre o correo…"
          className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-brand-500 sm:max-w-sm"
        />
        <span className="text-xs text-slate-400">
          {filteredParents.length} de {parents.length} clientes cargados
        </span>
        <button
          onClick={() => loadTables()}
          disabled={loadingTables}
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
        >
          {loadingTables ? 'Cargando…' : '↻ Recargar'}
        </button>
      </div>

      <div className="mt-3 overflow-x-auto rounded-2xl bg-white ring-1 ring-slate-200">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead className="border-b border-slate-100 text-xs uppercase text-slate-400">
            <tr>
              <th className="px-5 py-3">Cliente</th>
              <th className="px-5 py-3">Estado</th>
              <th className="px-5 py-3">Plan</th>
              <th className="px-5 py-3">Dispositivos</th>
              <th className="px-5 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredParents.map(({ uid, data }) => (
              <ParentRow
                key={uid}
                uid={uid}
                data={data}
                children={children}
                onChanged={() => loadTables()}
                currency={currency}
                isAdmin={uid === currentUserUid}
              />
            ))}
            {parents.length === 0 && (
              <tr>
                <td colSpan={5} className="px-5 py-8 text-center text-slate-400">
                  Sin clientes todavía
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {(hasMoreParents || hasMoreChildren) && (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          {hasMoreParents && (
            <button
              onClick={loadMoreParents}
              disabled={loadingTables}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              {loadingTables ? 'Cargando…' : `Cargar más clientes (${parents.length} cargados)`}
            </button>
          )}
          {hasMoreChildren && (
            <button
              onClick={loadMoreChildren}
              disabled={loadingTables}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              {loadingTables ? 'Cargando…' : `Cargar más dispositivos (${children.length} cargados)`}
            </button>
          )}
        </div>
      )}
    </Layout>
  )
}

function AdminStat({
  label,
  value,
  icon,
  accent,
}: {
  label: string
  value: number
  icon: string
  accent?: string
}) {
  return (
    <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <div className="flex items-center gap-3">
        <span className="text-2xl">{icon}</span>
        <div>
          <p className={`text-2xl font-bold ${accent ?? 'text-slate-800'}`}>{value}</p>
          <p className="text-xs text-slate-400">{label}</p>
        </div>
      </div>
    </div>
  )
}

function RequestRow({
  request,
  currency,
  onActivate,
  onDismiss,
}: {
  request: import('../lib/db').PaymentRequest
  currency: CurrencyCode
  onActivate: (uid: string, tier: 'premium' | 'familia') => void
  onDismiss: () => void
}) {
  const [tier, setTier] = useState<'premium' | 'familia'>('premium')
  // Triple verificación del monto: esperado (precio del plan pedido) vs
  // declarado por el cliente vs comprobante. Solo activar si coinciden.
  const expectedAmount =
    request.plan === 'premium' || request.plan === 'familia'
      ? PLAN_PRICES[request.plan as 'premium' | 'familia'][currency]
      : null
  const declaredAmount = typeof request.amountPaid === 'number' ? request.amountPaid : null
  const mismatch =
    expectedAmount !== null && declaredAmount !== null && declaredAmount !== expectedAmount
  return (
    <div className={`flex flex-wrap items-center justify-between gap-2 rounded-xl bg-white p-3 ring-1 ${mismatch ? 'ring-2 ring-rose-400' : 'ring-amber-200'}`}>
      <div>
        <p className="text-sm font-semibold text-slate-800">{request.name}</p>
        <p className="text-xs text-slate-400">{request.email}</p>
        <p className="mt-0.5 text-[11px] text-slate-400">
          {request.method === 'transfer' ? '🏦 Transferencia' : request.method === 'wompi' ? '💳 Wompi' : request.method === 'mercadopago' ? '💙 MercadoPago' : ''} · {request.plan ?? ''}
        </p>
        <p className="mt-1 text-xs">
          <span className="text-slate-500">Esperado: </span>
          <b className="text-slate-800">{expectedAmount !== null ? formatPrice(expectedAmount, currency) : '—'}</b>
          <span className="text-slate-400"> · </span>
          <span className="text-slate-500">Pagado: </span>
          <b className={mismatch ? 'text-rose-600' : 'text-slate-800'}>
            {declaredAmount !== null ? formatPrice(declaredAmount, currency) : '—'}
          </b>
        </p>
        {mismatch && (
          <p className="mt-0.5 text-[11px] font-bold text-rose-600">
            ⚠️ No coincide: verifica el comprobante y tu panel de Wompi/MP antes de activar.
          </p>
        )}
        {request.receiptImage && (
          <a href={request.receiptImage} target="_blank" rel="noreferrer">
            <img src={request.receiptImage} alt="Comprobante" className="mt-1 max-h-32 rounded-lg object-cover ring-1 ring-slate-200" />
            <p className="text-[10px] text-brand-500 hover:underline">Ver comprobante</p>
          </a>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={tier}
          onChange={(e) => setTier(e.target.value as 'premium' | 'familia')}
          className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs"
        >
          <option value="premium">⭐ Premium ({formatPrice(PLAN_PRICES.premium[currency], currency)})</option>
          <option value="familia">👨‍👩‍👦 Familia ({formatPrice(PLAN_PRICES.familia[currency], currency)})</option>
        </select>
        <button
          onClick={() => onActivate(request.uid, tier)}
          className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
        >
          ✅ Activar
        </button>
        <button
          onClick={onDismiss}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50"
        >
          Descartar
        </button>
      </div>
    </div>
  )
}

function ParentRow({
  uid,
  data,
  children,
  currency,
  isAdmin,
  onChanged,
}: {
  uid: string
  data: Parent
  children: { uid: string; data: Child }[]
  currency: CurrencyCode
  isAdmin: boolean
  onChanged: () => void
}) {
  const status = effectiveStatus(data.billing, isAdmin)
  const label = statusLabel(status)
  const days = daysRemaining(data.billing, isAdmin)
  const [editingBilling, setEditingBilling] = useState(false)
  const [billingDraft, setBillingDraft] = useState<BillingInfo>(data.billing ?? ({} as BillingInfo))
  const [renaming, setRenaming] = useState(false)
  const [nameDraft, setNameDraft] = useState(data.name ?? '')
  const [linkSel, setLinkSel] = useState('')

  const linked = children.filter(
    (c) => c.data.parentEmail?.toLowerCase() === data.email?.toLowerCase(),
  )
  const unlinked = children.filter(
    (c) => c.data.parentEmail?.toLowerCase() !== data.email?.toLowerCase(),
  )

  const mutate = async (op: () => Promise<unknown>, okMsg: string) => {
    try {
      await op()
      toast(okMsg, 'success')
      onChanged()
    } catch (e: unknown) {
      toast((e as Error).message ?? 'Operación fallida', 'error')
    }
  }

  const saveBilling = async () => {
    await mutate(async () => updateParentBilling(uid, billingDraft), '✅ Facturación guardada')
    setEditingBilling(false)
  }

  const saveName = async () => {
    await mutate(async () => setParentName(uid, nameDraft), '✅ Nombre guardado')
    setRenaming(false)
  }

  const doLink = async () => {
    if (!linkSel) return
    const limit = deviceLimitFor(data.billing)
    if (linked.length >= limit) {
      toast(
        `⛔ Límite del plan alcanzado (${limit} dispositivo${limit === 1 ? '' : 's'}). Mejora el plan del cliente para vincular más.`,
        'error',
      )
      setLinkSel('')
      return
    }
    const childUid = linkSel
    await mutate(async () => assignChildToParent(childUid, uid, data.email ?? ''), '✅ Dispositivo vinculado')
    setLinkSel('')
  }

  return (
    <tr>
      <td className="px-5 py-3 align-top">
        {renaming ? (
          <div className="flex items-center gap-2">
            <input
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              className="rounded-lg border border-slate-200 px-2 py-1"
            />
            <button onClick={saveName} className="rounded-lg bg-brand-600 px-2 py-1 text-xs font-semibold text-white">
              OK
            </button>
            <button onClick={() => setRenaming(false)} className="text-xs text-slate-400">✕</button>
          </div>
        ) : (
          <>
            <p className="font-semibold text-slate-800">{data.name}</p>
            <p className="text-xs text-slate-400">{data.email}</p>
          </>
        )}
      </td>
      <td className="px-5 py-3 align-top">
        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${label.cls}`}>{label.text}</span>
        {days > 0 && <p className="mt-1 text-xs text-slate-400">{days} días restantes</p>}
      </td>
      <td className="px-5 py-3 align-top">
        {editingBilling ? (
          <div className="space-y-1">
            <select
              value={billingDraft.plan}
              onChange={(e) => setBillingDraft({ ...billingDraft, plan: e.target.value as 'trial' | 'monthly' })}
              className="rounded-lg border border-slate-200 px-2 py-1 text-xs"
            >
              <option value="trial">Trial</option>
              <option value="monthly">Mensual</option>
            </select>
            <div className="flex gap-1">
              <button onClick={saveBilling} className="rounded-lg bg-emerald-600 px-2 py-1 text-xs font-semibold text-white">
                Guardar
              </button>
              <button onClick={() => setEditingBilling(false)} className="text-xs text-slate-400">✕</button>
            </div>
          </div>
        ) : (
          <>
            <p className="capitalize text-slate-700">
              {data.billing?.plan === 'monthly'
                ? `Mensual · ${data.billing.planTier === 'premium' ? 'Premium' : 'Familia'}`
                : data.billing?.plan ?? '—'}
            </p>
            <p className="text-xs text-slate-400">
              {data.billing?.plan === 'monthly'
                ? `${formatPrice(
                    data.billing.planTier === 'premium'
                      ? PLAN_PRICES.premium[currency]
                      : PLAN_PRICES.familia[currency],
                    currency,
                  )}/mes · hasta ${deviceLimitFor(data.billing)} dispositivos`
                : `hasta ${deviceLimitFor(data.billing)} dispositivo`}
            </p>
          </>
        )}
      </td>
      <td className="px-5 py-3 align-top">
        <div className="space-y-1">
          {linked.length === 0 && <p className="text-xs text-slate-400">Sin dispositivos</p>}
          {linked.map((c) => (
            <div key={c.uid} className="flex items-center gap-2 text-xs text-slate-600">
              <span>{c.data.deviceType === 'windows' ? '🖥️' : '🤖'}</span>
              <span className="truncate">{c.data.name} <span className="font-mono text-[10px] text-slate-400">v{c.data.agentVersion ?? '?'}</span></span>
              <button
                onClick={() => {
                  if (confirm(`¿Desvincular "${c.data.name}" de este cliente?`))
                    mutate(async () => unlinkChildFromParent(uid, c.uid), '🔗 Dispositivo desvinculado')
                }}
                className="text-amber-400 hover:text-amber-600"
                title="Desvincular (quita el dispositivo del cliente, no lo borra)"
              >
                🔗
              </button>
              <button
                onClick={() => {
                  if (confirm(`¿ELIMINAR PERMANENTEMENTE el dispositivo "${c.data.name}"?\n\nEsto borra el dispositivo de Firebase y no se puede deshacer.`)) {
                    mutate(async () => deleteChild(c.uid, uid), '🗑️ Dispositivo eliminado')
                  }
                }}
                className="text-rose-400 hover:text-rose-600"
                title="Eliminar dispositivo permanentemente"
              >
                🗑️
              </button>
            </div>
          ))}
          {unlinked.length > 0 && (
            <div className="flex items-center gap-1">
              <select
                value={linkSel}
                onChange={(e) => setLinkSel(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-1.5 py-1 text-xs"
              >
                <option value="">+ vincular…</option>
                {unlinked.map((c) => (
                  <option key={c.uid} value={c.uid}>
                    {c.data.name} ({c.data.deviceType ?? 'android'})
                  </option>
                ))}
              </select>
              <button
                onClick={doLink}
                className="rounded-lg bg-brand-600 px-2 py-1 text-xs font-semibold text-white"
              >
                OK
              </button>
            </div>
          )}
        </div>
      </td>
      <td className="px-5 py-3 align-top">
        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={() => { setBillingDraft(data.billing ?? ({} as BillingInfo)); setEditingBilling(true) }}
            className="rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
          >
            ✏️ Editar
          </button>
          <button
            onClick={() => mutate(async () => startTrial(uid), '🕑 Trial reiniciado (14 días)')}
            className="rounded-lg bg-sky-100 px-2 py-1 text-xs font-semibold text-sky-700 hover:bg-sky-200"
            title="Reiniciar trial de 14 días"
          >
            Trial
          </button>
          <button
            onClick={() => mutate(async () => activateMonthly(uid, 'premium'), '⭐ Premium activado')}
            className="rounded-lg bg-sky-100 px-2 py-1 text-xs font-semibold text-sky-700 hover:bg-sky-200"
            title={`Activar Premium (${formatPrice(PLAN_PRICES.premium[currency], currency)}/mes)`}
          >
            ⭐ {formatPrice(PLAN_PRICES.premium[currency], currency)}
          </button>
          <button
            onClick={() => mutate(async () => activateMonthly(uid, 'familia'), '👨‍👩‍👦 Plan Familia activado')}
            className="rounded-lg bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-200"
            title={`Activar Familia (${formatPrice(PLAN_PRICES.familia[currency], currency)}/mes)`}
          >
            👨‍👩‍👦 {formatPrice(PLAN_PRICES.familia[currency], currency)}
          </button>
          <button
            onClick={() => mutate(async () => suspendParent(uid), '⛔ Cliente suspendido')}
            className="rounded-lg bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-200"
          >
            ⛔
          </button>
          <button
            onClick={() => { setNameDraft(data.name ?? ''); setRenaming(true) }}
            className="rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
          >
            Nombre
          </button>
          <button
            onClick={() => {
              if (confirm(`¿Eliminar a ${data.name} y todos sus dispositivos?`))
                mutate(async () => deleteParent(uid, data.email ?? ''), '🗑️ Cliente eliminado')
            }}
            className="rounded-lg bg-rose-100 px-2 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-200"
          >
            🗑️
          </button>
        </div>
      </td>
    </tr>
  )
}
